/**
 * @file App - Frontend application runner.
 *
 * App is the frontend counterpart to Daemon. It provides:
 * - Schema-driven configuration with runtime validation
 * - Automatic infrastructure adoption (HttpClient, TelemetryService)
 * - React lifecycle integration via ReactProvider
 *
 * @example
 * ```typescript
 * const MyAppSchema = AppConfigSchema.extend({
 *   auth: z.object({ token: z.string().optional() }),
 * })
 *
 * class MyApp extends App<typeof MyAppSchema> {
 *   constructor(config: z.input<typeof MyAppSchema>) {
 *     super({ id: 'myapp', schema: MyAppSchema, config })
 *   }
 *
 *   async getHealth(): Promise<HealthResponse> {
 *     return this.http.request(HealthRoute)
 *   }
 * }
 * ```
 */

import { z, type ZodSchema } from 'zod'
import { FrontendService } from './frontend_service.ts'
import { HttpClient } from './services/http.ts'
import { TelemetryService } from './services/telemetry.ts'

////////////////////////////////////////////////////////////////////////////////
// Schemas
////////////////////////////////////////////////////////////////////////////////

/**
 * Base configuration schema for frontend applications.
 *
 * Extend this schema to add application-specific configuration:
 * ```typescript
 * const MyAppSchema = AppConfigSchema.extend({
 *   apiKey: z.string(),
 * })
 * ```
 *
 * - http.baseUrl: Base URL for API requests (e.g., 'https://api.example.com')
 * - http.timeout: Request timeout in milliseconds (default: 30000)
 * - http.headers: Default headers for all requests
 * - telemetry.level: Minimum log level to output (default: 'info')
 * - telemetry.console: Whether to output logs to console
 */
export const AppConfigSchema = z.object({
  http: z.object({
    baseUrl: z.string().url(),
    timeout: z.number().positive().optional().default(30_000),
    headers: z.record(z.string(), z.string()).optional(),
  }),
  telemetry: z
    .object({
      level: z
        .enum(['debug', 'info', 'warn', 'error'])
        .optional()
        .default('info'),
      console: z.boolean().optional(),
    })
    .optional()
    .prefault({}),
})

export type AppConfig = z.input<typeof AppConfigSchema>

////////////////////////////////////////////////////////////////////////////////
// Types
////////////////////////////////////////////////////////////////////////////////

/**
 * Props for App constructor.
 *
 * - id: Application identifier (used in logs/metrics)
 * - schema: Zod schema for runtime configuration validation
 * - config: Configuration values to validate against schema
 */
export type AppProps<TSchema extends ZodSchema> = {
  id: string
  schema: TSchema
  config: z.input<TSchema>
}

////////////////////////////////////////////////////////////////////////////////
// App
////////////////////////////////////////////////////////////////////////////////

/**
 * Frontend application runner.
 *
 * App manages the lifecycle of a frontend application, providing:
 * - Schema-validated configuration
 * - HTTP client for API requests
 * - Telemetry for client-side logging
 *
 * Extend this class to build application-specific clients:
 * ```typescript
 * class MyApp extends App<typeof MyAppSchema> {
 *   // Application-specific methods
 * }
 * ```
 */
export class App<
  TSchema extends ZodSchema = typeof AppConfigSchema,
> extends FrontendService {
  //////////////////////////////////////////////////////////////////////////////
  // Static Factory
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Create and start an App instance.
   *
   * Idiomatic public entry point for the frontend runtime — replaces
   * direct `new App(...)` + `configure()` + `start()` calls in workspace
   * code. Mirror of `Daemon.run()`'s shape on the backend.
   *
   * ```typescript
   * const app = await App.boot({
   *   id: 'my-app',
   *   schema: MyAppSchema,
   *   config: { http: { baseUrl: 'https://api.example.com' } },
   * })
   * ```
   *
   * @param props - App configuration
   * @returns Started App instance
   */
  static async boot<T extends ZodSchema>(props: AppProps<T>): Promise<App<T>> {
    const app = new App(props)
    await app.configure()
    await app.start()
    return app
  }

  /**
   * Legacy alias for {@link App.boot}.
   *
   * @deprecated Renamed to {@link App.boot}. Migrate consumers; this alias
   * stays until every workspace under `ts/apps/*` no longer references
   * `App.run`.
   */
  static async run<T extends ZodSchema>(props: AppProps<T>): Promise<App<T>> {
    return App.boot(props)
  }

  //////////////////////////////////////////////////////////////////////////////
  // Instance Properties
  //////////////////////////////////////////////////////////////////////////////

  /** The Zod schema used for configuration validation. */
  readonly schema: TSchema

  /** The validated configuration. */
  readonly config: z.output<TSchema> & z.output<typeof AppConfigSchema>

  //////////////////////////////////////////////////////////////////////////////
  // Constructor
  //////////////////////////////////////////////////////////////////////////////

  constructor(props: AppProps<TSchema>) {
    const config = props.schema.parse(props.config) as z.output<TSchema> &
      z.output<typeof AppConfigSchema>

    const http = new HttpClient({
      id: 'http',
      baseUrl: config.http.baseUrl,
      timeout: config.http.timeout,
      headers: config.http.headers,
    })

    super({ id: props.id }, http)

    this.schema = props.schema
    this.config = config

    this.adopt(http)
    this.adopt(
      new TelemetryService({
        id: 'telemetry',
        minLevel: config.telemetry?.level,
        console: config.telemetry?.console,
      }),
    )
  }

  //////////////////////////////////////////////////////////////////////////////
  // Public Methods
  //////////////////////////////////////////////////////////////////////////////

  override toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      baseUrl: this.config.http.baseUrl,
    }
  }
}
